import React, { useState } from "react";
import { Heart } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * P6.2 · Reusable wishlist toggle. Idempotent add/remove via
 * /api/wishlist and /api/wishlist/{id}. Cross-device: on mount, we do
 * NOT preload; instead the parent may pass `initialWishlisted` +
 * `initialWishlistId` to seed. Otherwise we lazy-check on first click.
 */
export default function WishlistToggle({
  objectType, objectId,
  initialWishlisted = false,
  initialWishlistId = null,
  size = 16,
  className = "",
}) {
  const [wl, setWl]       = useState(initialWishlisted);
  const [wid, setWid]     = useState(initialWishlistId);
  const [busy, setBusy]   = useState(false);

  const toggle = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (wl && wid) {
        await api.delete(`/wishlist/${wid}`);
        setWl(false); setWid(null);
        toast.success("Removed from wishlist");
      } else {
        const r = await api.post("/wishlist", {
          object_type: objectType, object_id: objectId,
        });
        const row = r.data?.wishlist;
        setWl(true); setWid(row?.wishlist_id || null);
        toast.success("Added to wishlist");
      }
    } catch (err) {
      if (err?.response?.status === 401) toast.error("Please log in.");
      else toast.error("Couldn't update wishlist");
    } finally { setBusy(false); }
  };

  return (
    <button
      onClick={toggle}
      disabled={busy}
      data-testid={`wishlist-toggle-${objectType}-${objectId}`}
      aria-label={wl ? "Remove from wishlist" : "Add to wishlist"}
      className={`p-1 rounded-full ${wl ? "text-rose-600" : "text-stone-400"} hover:bg-stone-100 ${className}`}
    >
      <Heart size={size} fill={wl ? "currentColor" : "none"}/>
    </button>
  );
}
